/**
 * Central permission & module code constants — the single frontend source of truth,
 * mirroring the backend `clinic_permissions` / `modules` catalog.
 *
 * The backend is authoritative (keys are DB data); these constants exist to eliminate
 * scattered string literals and typos when gating UI with `<PermissionGate>` / `can()`.
 * If the backend adds a code, add it here too.
 *
 * Gating policy is STRICT FAIL-CLOSED: if the user lacks the permission (or it is still
 * loading), the control is hidden. See `PermissionContext` (fail-closed empty set) and
 * `PermissionGate` (renders nothing while loading).
 */
export const PERM = {
  // ── OPD_CORE ──
  OPD_APPOINTMENTS_VIEW: 'OPD_APPOINTMENTS_VIEW',
  OPD_APPOINTMENTS_CREATE: 'OPD_APPOINTMENTS_CREATE',
  OPD_APPOINTMENTS_COMPLETE: 'OPD_APPOINTMENTS_COMPLETE',
  OPD_APPOINTMENTS_CANCEL: 'OPD_APPOINTMENTS_CANCEL',
  OPD_PRESCRIPTIONS_VIEW: 'OPD_PRESCRIPTIONS_VIEW',
  OPD_PRESCRIPTIONS_CREATE: 'OPD_PRESCRIPTIONS_CREATE',
  OPD_VITALS_RECORD: 'OPD_VITALS_RECORD',
  OPD_CLINICAL_NOTES_VIEW: 'OPD_CLINICAL_NOTES_VIEW',
  OPD_CLINICAL_NOTES_CREATE: 'OPD_CLINICAL_NOTES_CREATE',
  OPD_PATIENTS_VIEW: 'OPD_PATIENTS_VIEW',
  OPD_PATIENTS_CREATE: 'OPD_PATIENTS_CREATE',
  OPD_MEDICATIONS_VIEW: 'OPD_MEDICATIONS_VIEW',
  OPD_MEDICAL_RECORDS_VIEW: 'OPD_MEDICAL_RECORDS_VIEW',
  OPD_PATIENT_INTAKE_MANAGE: 'OPD_PATIENT_INTAKE_MANAGE',
  PATIENT_GLOBAL_SEARCH: 'PATIENT_GLOBAL_SEARCH',
  PATIENT_CONSENT_REQUEST: 'PATIENT_CONSENT_REQUEST',
  PATIENT_SHARED_HISTORY_VIEW: 'PATIENT_SHARED_HISTORY_VIEW',
  PATIENT_CONSENT_MANAGE: 'PATIENT_CONSENT_MANAGE',

  // ── BILLING ──
  BILLING_VIEW: 'BILLING_VIEW',
  BILLING_CREATE: 'BILLING_CREATE',
  BILLING_PAY: 'BILLING_PAY',
  BILLING_VOID: 'BILLING_VOID',
  BILLING_FEE_SCHEDULE_MANAGE: 'BILLING_FEE_SCHEDULE_MANAGE',

  // ── PHARMACY ──
  PHARMACY_ITEMS_VIEW: 'PHARMACY_ITEMS_VIEW',
  PHARMACY_ITEMS_MANAGE: 'PHARMACY_ITEMS_MANAGE',
  PHARMACY_DISPENSE: 'PHARMACY_DISPENSE',

  // ── LAB ──
  LAB_DEFINITIONS_VIEW: 'LAB_DEFINITIONS_VIEW',
  LAB_DEFINITIONS_MANAGE: 'LAB_DEFINITIONS_MANAGE',
  LAB_RESULTS_UPLOAD: 'LAB_RESULTS_UPLOAD',
  LAB_RESULTS_VIEW: 'LAB_RESULTS_VIEW',

  // ── REPORTS ──
  REPORTS_DAILY_VIEW: 'REPORTS_DAILY_VIEW',
  REPORTS_ANALYTICS_VIEW: 'REPORTS_ANALYTICS_VIEW',
  REPORTS_EXPORT: 'REPORTS_EXPORT',

  // ── FOLLOW_UPS ──
  FOLLOW_UPS_VIEW: 'FOLLOW_UPS_VIEW',
  FOLLOW_UPS_MANAGE: 'FOLLOW_UPS_MANAGE',

  // ── INPATIENT ──
  INPATIENT_VIEW: 'INPATIENT_VIEW',
  INPATIENT_ADMISSIONS_MANAGE: 'INPATIENT_ADMISSIONS_MANAGE',
  INPATIENT_WARDS_MANAGE: 'INPATIENT_WARDS_MANAGE',

  // ── ADMIN ──
  ADMIN_USERS_MANAGE: 'ADMIN_USERS_MANAGE',
  ADMIN_CLINIC_SETTINGS: 'ADMIN_CLINIC_SETTINGS',
  ADMIN_INVITATIONS_MANAGE: 'ADMIN_INVITATIONS_MANAGE',
} as const;

export const MODULE = {
  OPD_CORE: 'OPD_CORE',
  BILLING: 'BILLING',
  LAB: 'LAB',
  PHARMACY: 'PHARMACY',
  INPATIENT: 'INPATIENT',
  FOLLOW_UPS: 'FOLLOW_UPS',
  REPORTS: 'REPORTS',
  AI_ASSISTANT: 'AI_ASSISTANT',
} as const;

export type PermissionCode = (typeof PERM)[keyof typeof PERM];
export type ModuleCode = (typeof MODULE)[keyof typeof MODULE];
